/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FlatList, RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { ticketColors } from '../../shared/theme/ticketColors'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { TicketDeskFooter } from '../../shared/ticket/TicketDeskFooter';
import { VIRTUALIZED_LIST_PROPS } from '../../shared/ui/virtualizedListProps';
import { FilterSheetButton } from '../../shared/filters/FilterSheetButton'
import { FilterSheetModal } from '../../shared/filters/FilterSheetModal'
import { OperationalFilterSegmentedRow } from '../../shared/filters/OperationalFilterPrimitives';
import { ExpensesScreenHeader } from '../components/ExpensesScreenHeader';
import { ExpensesFiltersPanel } from '../components/ExpensesFiltersPanel';
import { ExpenseRowCard } from '../components/ExpenseRowCard';
import { ExpensesTable } from '../components/ExpensesTable';
import { ExpenseDetailModal } from '../components/ExpenseDetailModal';
import { ExpensePaymentHistoryRowCard } from '../components/ExpensePaymentHistoryRowCard';
import { ExpensePaymentsHistoryPanel, ExpensePaymentsListEmpty } from '../components/ExpensePaymentsHistoryPanel';
import { ExpensePaymentsFiltersPanel } from '../components/ExpensePaymentsFiltersPanel';
import { ExpenseRegisterModal } from '../components/ExpenseRegisterModal';
import { ExpenseSettleModal } from '../components/ExpenseSettleModal';
import type { ExpenseRow, ExpenseSettlementListRow } from '../domain/expenseTypes';
import { expenseErpCopy } from '../domain/expenseErpCopy'
import { expenseAmountResidual } from '../domain/expenseLabels';
import { useExpensesScreen } from './useExpensesScreen';

const LIST_WEB_EXTRA_BOTTOM = 16;

const VIEW_OPTIONS = [
  { value: 'expenses' as const, label: expenseErpCopy.tabBills },
  { value: 'payments' as const, label: expenseErpCopy.tabPayments },
];

export function ExpensesScreen() {
  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const vm = useExpensesScreen();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { layoutWide } = useContentLayout('dataWide');
  const listFlow = vm.listFlow;

  const [settleAmountText, setSettleAmountText] = useState('');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'Expenses',
    async (opts) => { await vm.loadFn(opts); },
    { minFocusIntervalMs: 60_000 }
  );

  const openSettle = useCallback(
    (expense: ExpenseRow) => {
      setSettleAmountText(String(expenseAmountResidual(expense.amount, expense.amountPaid)));
      void vm.prepareSettle(expense);
    },
    [vm]
  );

  const filterActiveCount =
    vm.screenView === 'expenses' ? listFlow.activeFilterCount : vm.paymentsFlow.activeFilterCount;

  const listHeader = useMemo(
    () => (
      <>
        <ExpensesScreenHeader
          summary={vm.expenseKpi.summary}
          paymentsSummary={vm.paymentsKpi.summary}
          searchQuery={vm.screenView === 'payments' ? vm.paymentsFlow.searchQuery : listFlow.searchQuery}
          onSearchQueryChange={
            vm.screenView === 'payments'
              ? vm.paymentsFlow.setSearchQuery
              : listFlow.setSearchQuery
          }
          canEdit={vm.canEdit}
          canExport={vm.canExport}
          exporting={vm.exporting}
          kpiLoading={
            vm.screenView === 'payments'
              ? vm.paymentsKpi.loading
              : vm.expenseKpi.loading
          }
          kpiError={
            vm.screenView === 'payments'
              ? vm.paymentsKpi.error
              : vm.expenseKpi.error
          }
          onNewExpense={() => vm.setRegisterOpen(true)}
          onExportCsv={() => void vm.handleExportCsv()}
          activeCashSessionId={vm.activeCashSessionId}
          onOpenCash={() => navigation.navigate('CashManagement' as never)}
          paymentsMode={vm.screenView === 'payments'}
          onExportPaymentsCsv={() => void vm.handleExportPaymentsCsv()}
          kpiPeriodLabel={
            vm.screenView === 'payments' ? vm.paymentsFlow.periodLabel : listFlow.periodLabel
          }
          onOverdueKpiPress={
            vm.screenView === 'expenses'
              ? () =>
                  listFlow.setDueStatusFilter(
                    listFlow.dueStatusFilter === 'overdue' ? 'all' : 'overdue'
                  )
              : undefined
          }
          overdueKpiSelected={vm.screenView === 'expenses' && listFlow.dueStatusFilter === 'overdue'}
          filterTrigger={
            <FilterSheetButton
              activeCount={filterActiveCount}
              onOpen={() => setFilterSheetOpen(true)}
              variant={layoutWide ? 'desk' : 'ticket'}
              style={layoutWide ? undefined : styles.filterTriggerMobile}
            />
          }
        />
        <View style={styles.viewToggleRow}>
          <OperationalFilterSegmentedRow
            ticketTone
            options={VIEW_OPTIONS}
            value={vm.screenView}
            onChange={vm.setScreenView}
          />
        </View>
      </>
    ),
    [listFlow, vm, navigation, layoutWide, filterActiveCount]
  );

  const pagination = (
    <TicketDeskFooter
      pageIndex={listFlow.expensePageIndex}
      pageSize={listFlow.expensePageSize}
      productCount={listFlow.expenses.length}
      totalCount={listFlow.expenseTotalCount}
      hasMore={listFlow.expenseHasMore}
      loading={vm.effectiveExpenseLoading}
      onPreviousPage={listFlow.loadExpensePreviousPage}
      onNextPage={listFlow.loadExpenseNextPage}
      embedded
    />
  );

  const billsSearching = listFlow.searchQuery.trim().length > 0;
  const billsEmptyCopy = billsSearching
    ? { title: expenseErpCopy.emptyBillsSearchTitle, subtitle: expenseErpCopy.emptyBillsSearchSubtitle }
    : listFlow.hasActiveFilters
      ? { title: expenseErpCopy.emptyBillsFilteredTitle, subtitle: expenseErpCopy.emptyBillsFilteredSubtitle }
      : { title: expenseErpCopy.emptyBillsTitle, subtitle: expenseErpCopy.emptyBillsSubtitle };

  const emptyList = listFlow.expenseListError ? (
    <ModuleEmptyState
      variant="nest"
      title="No pude cargar las facturas"
      subtitle={listFlow.expenseListError}
      primaryAction={{ label: 'Reintentar', onPress: () => void listFlow.reloadExpensesFromStart() }}
      testID="gastos.error"
    />
  ) : (
    <ModuleEmptyState
      variant="nest"
      loading={vm.effectiveExpenseLoading}
      loadingText="Cargando facturas…"
      title={billsEmptyCopy.title}
      subtitle={billsEmptyCopy.subtitle}
      primaryAction={
        !listFlow.hasActiveFilters && vm.canEdit
          ? { label: expenseErpCopy.actionNewBill, onPress: () => vm.setRegisterOpen(true) }
          : undefined
      }
      secondaryAction={
        listFlow.hasActiveFilters
          ? { label: expenseErpCopy.clearFiltersAction, onPress: listFlow.clearFilters }
          : undefined
      }
      testID="gastos.empty"
    />
  );

  const paymentPagination = (
    <TicketDeskFooter
      pageIndex={vm.paymentsFlow.pageIndex}
      pageSize={vm.paymentsFlow.pageSize}
      productCount={vm.paymentsFlow.rows.length}
      totalCount={vm.paymentsFlow.total}
      hasMore={vm.paymentsFlow.hasMore}
      loading={vm.paymentsFlow.loading}
      onPreviousPage={vm.paymentsFlow.loadPreviousPage}
      onNextPage={vm.paymentsFlow.loadNextPage}
      embedded
    />
  );

  const paymentEmptyList = <ExpensePaymentsListEmpty flow={vm.paymentsFlow} />;

  const renderItem = useCallback(
    ({ item }: { item: ExpenseRow }) => (
      <ExpenseRowCard item={item} onOpenDetail={vm.handleOpenDetail} />
    ),
    [vm.handleOpenDetail]
  );

  const paymentRenderItem = useCallback(
    ({ item }: { item: ExpenseSettlementListRow }) => (
      <ExpensePaymentHistoryRowCard
        row={item}
        onPress={() => void vm.handleOpenPaymentFromHistory(item)}
      />
    ),
    [vm]
  );

  const listContentStyle = useMemo(
    () => ({
      paddingBottom: insets.bottom + LIST_WEB_EXTRA_BOTTOM,
      flexGrow:
        (vm.screenView === 'expenses' && listFlow.expenses.length === 0) ||
        (vm.screenView === 'payments' && vm.paymentsFlow.rows.length === 0)
          ? 1
          : undefined,
    }),
    [insets.bottom, listFlow.expenses.length, vm.paymentsFlow.rows.length, vm.screenView]
  );

  const refreshControl = (
    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={ticketColors.gold} />
  );

  if (!vm.moduleEnabled) {
    return (
      <ShellContentFrame style={styles.shell}>
        <ModuleEmptyState
          title="Gastos no disponible"
          subtitle="Por ahora no está habilitado."
          testID="gastos.unavailable"
        />
      </ShellContentFrame>
    );
  }

  if (!vm.canView) {
    return (
      <ShellContentFrame style={styles.shell}>
        <ModuleEmptyState
          title="Sin acceso"
          subtitle="No podés ver Gastos con tu rol. Pedile al dueño que te habilite el permiso."
          testID="gastos.no-permission"
        />
      </ShellContentFrame>
    );
  }

  const isPaymentsView = vm.screenView === 'payments';

  return (
    <ShellContentFrame style={[styles.shell, !layoutWide && styles.shellCompact]}>
      {layoutWide ? (
        <ScrollView
          style={styles.listFlex}
          ref={isPaymentsView ? undefined : listFlow.expenseWideScrollRef}
          contentContainerStyle={listContentStyle}
          refreshControl={refreshControl}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {listHeader}
          {isPaymentsView ? (
            <>
              <ExpensePaymentsHistoryPanel
                flow={vm.paymentsFlow}
                layoutWide
                onOpenPayment={(row) => void vm.handleOpenPaymentFromHistory(row)}
              />
              {paymentPagination}
            </>
          ) : (
            <>
              <PaperSheet style={{ minHeight: 0 }} testID="gastos.list.sheet">
                <ExpensesTable expenses={listFlow.expenses} onOpenDetail={vm.handleOpenDetail} />
                {listFlow.expenses.length === 0 ? emptyList : null}
              </PaperSheet>
              {pagination}
            </>
          )}
        </ScrollView>
      ) : isPaymentsView ? (
        <FlatList
          style={styles.listFlex}
          data={vm.paymentsFlow.rows}
          keyExtractor={(item) => item.settlementId}
          contentContainerStyle={listContentStyle}
          refreshControl={refreshControl}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          ListHeaderComponent={listHeader}
          ListFooterComponent={paymentPagination}
          ListEmptyComponent={paymentEmptyList}
          renderItem={paymentRenderItem}
          ItemSeparatorComponent={null}
          {...VIRTUALIZED_LIST_PROPS}
        />
      ) : (
        <FlatList
          style={styles.listFlex}
          ref={listFlow.expenseListRef}
          data={listFlow.expenses}
          keyExtractor={(item) => item.id}
          contentContainerStyle={listContentStyle}
          refreshControl={refreshControl}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          ListHeaderComponent={listHeader}
          ListFooterComponent={pagination}
          ListEmptyComponent={emptyList}
          renderItem={renderItem}
          ItemSeparatorComponent={null}
          {...VIRTUALIZED_LIST_PROPS}
        />
      )}

      <FilterSheetModal
        visible={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        onApply={() => setFilterSheetOpen(false)}
        appearance="ticket"
        kicker={vm.screenView === 'expenses' ? 'Gastos' : 'Gastos · Pagos'}
        title="Filtros"
        subtitle={
          vm.screenView === 'expenses'
            ? 'Acotá la lista de facturas.'
            : 'Acotá el historial de pagos.'
        }
        onClear={() => {
          if (vm.screenView === 'expenses') {
            listFlow.clearFilters();
          } else {
            vm.paymentsFlow.clearFilters();
          }
        }}
        hasActiveDraft={
          vm.screenView === 'expenses'
            ? listFlow.hasActiveFilters
            : vm.paymentsFlow.hasActiveFilters
        }
      >
        {vm.screenView === 'expenses' ? (
          <ExpensesFiltersPanel
            periodPreset={listFlow.periodPreset}
            onPeriodPresetChange={listFlow.setPeriodPreset}
            customFromDate={listFlow.customFromDate}
            customToDate={listFlow.customToDate}
            onCustomFromDateChange={listFlow.setCustomFromDate}
            onCustomToDateChange={listFlow.setCustomToDate}
            paymentStatusFilter={listFlow.paymentStatusFilter}
            onPaymentStatusFilterChange={listFlow.setPaymentStatusFilter}
            categoryFilter={listFlow.categoryFilter}
            onCategoryFilterChange={listFlow.setCategoryFilter}
            categories={vm.categories}
            supplierFilter={listFlow.supplierFilter}
            onSupplierFilterChange={listFlow.setSupplierFilter}
            suppliers={vm.suppliers}
            sortBy={listFlow.sortBy}
            onSortByChange={listFlow.setSortBy}
            dueStatusFilter={listFlow.dueStatusFilter}
            onDueStatusFilterChange={listFlow.setDueStatusFilter}
            hasActiveFilters={listFlow.hasActiveFilters}
            onClearFilters={listFlow.clearFilters}
          />
        ) : (
          <ExpensePaymentsFiltersPanel flow={vm.paymentsFlow} />
        )}
      </FilterSheetModal>

      {vm.detailTarget ? (
        <ExpenseDetailModal
          visible
          expense={vm.detailTarget}
          canEdit={vm.canEdit}
          canPay={vm.canPay}
          canVoid={vm.canVoid}
          submitting={vm.submitting}
          resolveUserLabel={vm.resolveUserLabel}
          organizationId={vm.organizationId ?? undefined}
          branchId={vm.currentBranchId ?? undefined}
          createdBy={vm.userId ?? null}
          onClose={() => vm.setDetailTarget(null)}
          onPay={(expense) => {
            vm.setDetailTarget(null);
            openSettle(expense);
          }}
          onConfirmVoid={(expense, reason) => void vm.handleVoid(expense, reason)}
          onVoidSettlement={(settlementId, reason) => void vm.handleVoidSettlement(settlementId, reason)}
        />
      ) : null}

      {vm.registerOpen ? (
        <ExpenseRegisterModal
          visible
          submitting={vm.submitting}
          suppliers={vm.suppliers}
          fiscalCaptureEnabled={vm.fiscalCaptureEnabled}
          branchId={vm.currentBranchId ?? null}
          activeCashSession={vm.activeCashSessionInfo}
          onClose={() => vm.setRegisterOpen(false)}
          onSubmit={(input) => void vm.handleCreate(input)}
          onOpenCash={() => navigation.navigate('CashManagement' as never)}
        />
      ) : null}

      {vm.settleTarget ? (
        <ExpenseSettleModal
          visible
          expense={vm.settleTarget}
          amountText={settleAmountText}
          onAmountTextChange={setSettleAmountText}
          activeCashSession={vm.activeCashSessionInfo}
          submitting={vm.submitting}
          onClose={() => vm.setSettleTarget(null)}
          onOpenCash={() => navigation.navigate('CashManagement' as never)}
          canPayWithCheck={vm.canPayWithCheck}
          checkOptions={vm.portfolioChecks}
          checksLoading={vm.portfolioChecksLoading}
          onConfirm={({ method, note, paidDate, checkId }) => {
            if (!vm.settleTarget) return;
            void vm.handleSettle({
              expense: vm.settleTarget,
              method,
              amountText: settleAmountText,
              note,
              paidDate,
              checkId,
            });
          }}
        />
      ) : null}

      {vm.ConfirmModal}
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  shell: {
    ...ticketSurfaces.screen,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  shellCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  listFlex: { flex: 1 },
  viewToggleRow: {
    paddingBottom: spacing.sm,
  },
  filterTriggerMobile: {
    flex: 1,
    marginBottom: 0,
    // Tap target ≥44px — el botón ticket compartido tiene height:42.
    height: 44,
  },
});
